import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import ContractorRegistrationApiService from "../../../../../infrastructure/api/contractorRegistration";
import DefaultApiService from "../../../../../infrastructure/api/default";
import {
  companyNationalIdInputProps,
  yupCompanyNationalIdOptional,
} from "../../../../../infrastructure/utils/companyNationalIdRules";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import { getApiFileBaseUrl, resolveUploadUrl } from "../../../../../infrastructure/utils/resolveUploadUrl";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import { formatLocalDateForApi, toLocalDateInputValue } from "../../../../middleware/middleware";

const CARD_SX = {
  borderRadius: "16px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)",
};

const CONTRACTOR_STATUS_MAP = {
  1: { label: "در انتظار بررسی", color: "warning" },
  2: { label: "تایید شده", color: "success" },
  3: { label: "رد شده", color: "error" },
  4: { label: "غیرفعال", color: "default" },
};

const REGISTRATION_STATUS_MAP = {
  1: { label: "در انتظار بررسی", color: "warning" },
  2: { label: "در حال بررسی", color: "info" },
  3: { label: "تایید شده", color: "success" },
  4: { label: "رد شده", color: "error" },
  5: { label: "نیاز به اطلاعات تکمیلی", color: "secondary" },
  6: { label: "مدارک تایید شد - در انتظار قرارداد", color: "primary" },
};

const validationSchema = Yup.object().shape({
  title: Yup.string().required("نام شرکت الزامی است."),
  mobile: Yup.string()
    .required("شماره موبایل الزامی است.")
    .matches(/^09\d{9}$/, "شماره موبایل معتبر نیست"),
  email: Yup.string().email("ایمیل معتبر نیست").nullable(),
  about: Yup.string().required("متن معرفی الزامی است."),
  companyNationalId: yupCompanyNationalIdOptional(),
});

const toDateInputValue = (value) => toLocalDateInputValue(value);

const parseSpecialties = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return String(value)
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  }
};

const DocumentViewCard = ({ title, url, baseUrl }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Chip
      label={url ? "بارگذاری شده" : "بارگذاری نشده"}
      color={url ? "success" : "error"}
      size="small"
      sx={{ mb: 1.5 }}
    />
    <Box sx={{ display: "flex", gap: 1 }}>
      {url ? (
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            href={resolveUploadUrl(url, baseUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            مشاهده
          </Button>
          <IconButton
            size="small"
            href={resolveUploadUrl(url, baseUrl)}
            download
            component="a"
          >
            <DownloadIcon />
          </IconButton>
        </>
      ) : (
        <CancelIcon color="disabled" />
      )}
    </Box>
  </Paper>
);

const ContractorInfoModifyPage = () => {
  const data = useLoaderData("root");
  const navigate = useNavigate();
  const { id: roleId } = useParams();

  const baseUrl = getApiFileBaseUrl(data);
  const contractorInfo = data?.contractorInfo || {};
  const registration = data?.registration || null;
  const isReadOnly = !!data?.isReadOnly;

  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [selectedProvinceId, setSelectedProvinceId] = useState(
    contractorInfo?.provinceId || null
  );

  useEffect(() => {
    let mounted = true;
    const loadMeta = async () => {
      try {
        const [provincesRes, citiesRes, specialtiesRes] = await Promise.all([
          ContractorRegistrationApiService.getProvinces(),
          ContractorRegistrationApiService.getCities(),
          ContractorRegistrationApiService.getSpecialties(),
        ]);
        if (!mounted) return;
        setProvinces(provincesRes?.data || provincesRes || []);
        setAllCities(citiesRes?.data || citiesRes || []);
        setSpecialties(specialtiesRes?.data || specialtiesRes || []);
      } catch (error) {
        console.error(error);
        toast.error("خطا در بارگذاری اطلاعات پایه");
      } finally {
        if (mounted) setLoadingMeta(false);
      }
    };
    loadMeta();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredCities = useMemo(() => {
    if (!selectedProvinceId) return [];
    return allCities.filter(
      (city) => Number(city.provinceId) === Number(selectedProvinceId)
    );
  }, [allCities, selectedProvinceId]);

  const contractorStatus = CONTRACTOR_STATUS_MAP[contractorInfo?.contractorInfoStatus] || {
    label: contractorInfo?.contractorInfoStatusTitle || "در انتظار بررسی",
    color: "warning",
  };

  const registrationStatus = registration
    ? REGISTRATION_STATUS_MAP[registration.status] || {
        label: registration.statusTitle || "نامشخص",
        color: "default",
      }
    : null;

  const onNavigateToListPage = () => {
    navigate("/SLN/Contractor/ContractorInfo");
  };

  const onSubmit = async (formValue, { setSubmitting }) => {
    if (isReadOnly) return;
    setLoading(true);
    const sendData = {
      ...formValue,
      birthDate: formatLocalDateForApi(formValue.birthDate),
      specialties: JSON.stringify(formValue.specialtyIds || []),
    };
    delete sendData.specialtyIds;

    try {
      const res = await DefaultApiService.modifyApi(
        "SLN/Contractor/ContractorInfo",
        sendData
      );
      if (res.isSuccess) {
        toast.success(res.message || "اطلاعات با موفقیت ذخیره شد");
        onNavigateToListPage();
      } else {
        toast.error(res.message || "خطا در ذخیره اطلاعات");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const initialValues = {
    ...contractorInfo,
    birthDate: toDateInputValue(contractorInfo?.birthDate),
    specialtyIds: parseSpecialties(contractorInfo?.specialties),
  };

  const pageTitle = isReadOnly
    ? `مشاهده ${contractorInfo?.title || "اطلاعات پیمانکار"}`
    : roleId !== "create"
      ? `ویرایش ${contractorInfo?.title || "اطلاعات پیمانکار"}`
      : "تکمیل اطلاعات پیمانکار";

  return (
    <Box dir="rtl" sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <BreadcrumbsComponent />
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="h5" fontWeight={700}>
              {pageTitle}
            </Typography>
            <Chip
              size="small"
              color={contractorStatus.color}
              label={`وضعیت همکاری: ${contractorStatus.label}`}
            />
            {registrationStatus && (
              <Chip
                size="small"
                color={registrationStatus.color}
                variant="outlined"
                label={`وضعیت ثبت‌نام: ${registrationStatus.label}`}
              />
            )}
          </Stack>
        </Grid>

        {isReadOnly && (
          <Grid item xs={12}>
            <Alert severity="info" icon={<CheckCircleIcon />}>
              اطلاعات شما تأیید نهایی شده است. این صفحه فقط برای مشاهده است و امکان ویرایش وجود ندارد.
            </Alert>
          </Grid>
        )}

        {registration?.rejectionReason && (
          <Grid item xs={12}>
            <Alert severity="error">
              دلیل رد: {registration.rejectionReason}
            </Alert>
          </Grid>
        )}

        {registration?.reviewNotes && (
          <Grid item xs={12}>
            <Alert severity="warning">
              یادداشت بررسی: {registration.reviewNotes}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card sx={CARD_SX}>
            <CardHeader title={pageTitle} />
            <CardContent>
              {loadingMeta ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                  enableReinitialize
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue,
                  }) => {
                    const fieldProps = (name) => ({
                      name,
                      fullWidth: true,
                      value: values[name] ?? "",
                      error: Boolean(errors[name] && touched[name]),
                      helperText: errors[name] && touched[name] ? errors[name] : undefined,
                      variant: "outlined",
                      disabled: isReadOnly,
                      InputProps: isReadOnly ? { readOnly: true } : undefined,
                    });

                    return (
                      <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                          {/* اطلاعات شرکت */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardHeader title="اطلاعات شرکت" />
                              <CardContent>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="نام شرکت"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("title")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="شناسه ملی شرکت"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      inputProps={companyNationalIdInputProps}
                                      {...fieldProps("companyNationalId")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="شماره ثبت شرکت"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("companyRegistrationNumber")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="تلفن شرکت"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("companyPhone")}
                                    />
                                  </Grid>
                                  {registration?.foundedYear && (
                                    <Grid item xs={12} md={6}>
                                      <TextField
                                        label="سال تاسیس"
                                        value={registration.foundedYear}
                                        disabled
                                        fullWidth
                                        variant="outlined"
                                      />
                                    </Grid>
                                  )}
                                  {registration?.companyWebsite && (
                                    <Grid item xs={12} md={6}>
                                      <TextField
                                        label="وب‌سایت"
                                        value={registration.companyWebsite}
                                        disabled
                                        fullWidth
                                        variant="outlined"
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* اطلاعات تماس */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardHeader title="اطلاعات تماس و نماینده" />
                              <CardContent>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="نام"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("firstName")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="نام خانوادگی"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("lastName")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="کد ملی"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("nationalCode")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="موبایل"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("mobile")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="تلفن"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("telephon")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="ایمیل"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("email")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    {isReadOnly ? (
                                      <TextField
                                        label="تاریخ تولد"
                                        value={values.birthDate || ""}
                                        disabled
                                        fullWidth
                                        variant="outlined"
                                      />
                                    ) : (
                                      <JalaliDatePicker
                                        label="تاریخ تولد"
                                        value={values.birthDate}
                                        onChange={(val) => setFieldValue("birthDate", val)}
                                      />
                                    )}
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* آدرس */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardHeader title="آدرس و موقعیت" />
                              <CardContent>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    {isReadOnly ? (
                                      <TextField
                                        label="استان"
                                        value={values.province || ""}
                                        disabled
                                        fullWidth
                                        variant="outlined"
                                      />
                                    ) : (
                                      <FormControl fullWidth variant="outlined">
                                        <InputLabel>استان</InputLabel>
                                        <Select
                                          label="استان"
                                          value={values.provinceId ?? ""}
                                          onChange={(e) => {
                                            const provinceId = e.target.value;
                                            const province = provinces.find(
                                              (p) => Number(p.id) === Number(provinceId)
                                            );
                                            setFieldValue("provinceId", provinceId || null);
                                            setFieldValue("province", province?.title || "");
                                            setFieldValue("city", "");
                                            setSelectedProvinceId(provinceId || null);
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>انتخاب کنید</em>
                                          </MenuItem>
                                          {provinces.map((province) => (
                                            <MenuItem key={province.id} value={province.id}>
                                              {province.title}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    {isReadOnly ? (
                                      <TextField
                                        label="شهر"
                                        value={values.city || ""}
                                        disabled
                                        fullWidth
                                        variant="outlined"
                                      />
                                    ) : (
                                      <FormControl fullWidth variant="outlined">
                                        <InputLabel>شهر</InputLabel>
                                        <Select
                                          label="شهر"
                                          value={values.city || ""}
                                          onChange={(e) => setFieldValue("city", e.target.value)}
                                          disabled={!selectedProvinceId}
                                        >
                                          <MenuItem value="">
                                            <em>انتخاب کنید</em>
                                          </MenuItem>
                                          {filteredCities.map((city) => (
                                            <MenuItem key={city.id} value={city.cityName}>
                                              {city.cityName}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="کد پستی"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("postalCode")}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      label="آدرس"
                                      multiline
                                      minRows={2}
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("address")}
                                    />
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* تخصص */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardHeader title="تخصص و معرفی" />
                              <CardContent>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="سابقه کار (سال)"
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("workExperience")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={8}>
                                    {isReadOnly ? (
                                      <TextField
                                        label="تخصص‌ها"
                                        value={(values.specialtyIds || []).join("، ")}
                                        disabled
                                        fullWidth
                                        variant="outlined"
                                      />
                                    ) : (
                                      <FormControl fullWidth variant="outlined">
                                        <InputLabel>تخصص‌ها</InputLabel>
                                        <Select
                                          multiple
                                          label="تخصص‌ها"
                                          value={values.specialtyIds || []}
                                          onChange={(e) =>
                                            setFieldValue("specialtyIds", e.target.value)
                                          }
                                          renderValue={(selected) =>
                                            selected
                                              .map(
                                                (id) =>
                                                  specialties.find((s) => String(s.id) === String(id))
                                                    ?.title || id
                                              )
                                              .join("، ")
                                          }
                                        >
                                          {specialties.map((item) => (
                                            <MenuItem key={item.id} value={String(item.id)}>
                                              {item.title}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                        <FormHelperText>می‌توانید چند مورد انتخاب کنید</FormHelperText>
                                      </FormControl>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="متن معرفی"
                                      multiline
                                      minRows={3}
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("about")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="توضیحات"
                                      multiline
                                      minRows={3}
                                      onChange={wrapFormikHandleChange(handleChange)}
                                      {...fieldProps("description")}
                                    />
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* مدارک */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardHeader title="مدارک و گواهی‌ها" />
                              <CardContent>
                                {isReadOnly ? (
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <DocumentViewCard
                                        title="تصویر کارت ملی"
                                        url={values.nationalCardImageUrl}
                                        baseUrl={baseUrl}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <DocumentViewCard
                                        title="مجوز شرکت"
                                        url={values.companyLicenseImageUrl}
                                        baseUrl={baseUrl}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <DocumentViewCard
                                        title="بیمه"
                                        url={values.insuranceDocumentUrl}
                                        baseUrl={baseUrl}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <DocumentViewCard
                                        title="گواهینامه فنی"
                                        url={values.certificateUrl}
                                        baseUrl={baseUrl}
                                      />
                                    </Grid>
                                  </Grid>
                                ) : (
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <FileUploader
                                        moduleName="SLN/Contractor"
                                        maxFiles={1}
                                        label="تصویر کارت ملی"
                                        baseurl={baseUrl}
                                        accept="image/*,.pdf"
                                        value={values.nationalCardImageUrl}
                                        onChange={(value) =>
                                          setFieldValue("nationalCardImageUrl", value || "")
                                        }
                                        placeholder="تصویر کارت ملی را انتخاب کنید"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <FileUploader
                                        moduleName="SLN/Contractor"
                                        maxFiles={1}
                                        label="مجوز شرکت"
                                        baseurl={baseUrl}
                                        accept="image/*,.pdf"
                                        value={values.companyLicenseImageUrl}
                                        onChange={(value) =>
                                          setFieldValue("companyLicenseImageUrl", value || "")
                                        }
                                        placeholder="مجوز شرکت را انتخاب کنید"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <FileUploader
                                        moduleName="SLN/Contractor"
                                        maxFiles={1}
                                        label="بیمه"
                                        baseurl={baseUrl}
                                        accept="image/*,.pdf"
                                        value={values.insuranceDocumentUrl}
                                        onChange={(value) =>
                                          setFieldValue("insuranceDocumentUrl", value || "")
                                        }
                                        placeholder="مدرک بیمه را انتخاب کنید"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <FileUploader
                                        moduleName="SLN/Contractor"
                                        maxFiles={1}
                                        label="گواهینامه فنی"
                                        baseurl={baseUrl}
                                        accept="image/*,.pdf"
                                        value={values.certificateUrl}
                                        onChange={(value) =>
                                          setFieldValue("certificateUrl", value || "")
                                        }
                                        placeholder="گواهینامه فنی را انتخاب کنید"
                                      />
                                    </Grid>
                                  </Grid>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* مدارک ثبت‌نام (فقط مشاهده) */}
                          {registration && (
                            <Grid item xs={12}>
                              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardHeader title="مدارک ثبت‌نام (فقط مشاهده)" />
                                <CardContent>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <DocumentViewCard
                                        title="کارت ملی (ثبت‌نام)"
                                        url={registration.nationalCardImageUrl}
                                        baseUrl={baseUrl}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <DocumentViewCard
                                        title="مجوز شرکت (ثبت‌نام)"
                                        url={registration.companyLicenseImageUrl}
                                        baseUrl={baseUrl}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <DocumentViewCard
                                        title="بیمه (ثبت‌نام)"
                                        url={registration.insuranceDocumentUrl}
                                        baseUrl={baseUrl}
                                      />
                                    </Grid>
                                    {registration.registrationType === "legal" && (
                                      <>
                                        <Grid item xs={12} sm={6} md={4}>
                                          <DocumentViewCard
                                            title="مدارک ثبتی شرکت"
                                            url={registration.officialRegistrationDocumentsUrl}
                                            baseUrl={baseUrl}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                          <DocumentViewCard
                                            title="مدارک صاحبان امضا"
                                            url={registration.authorizedSignatoriesDocumentUrl}
                                            baseUrl={baseUrl}
                                          />
                                        </Grid>
                                      </>
                                    )}
                                  </Grid>
                                </CardContent>
                              </Card>
                            </Grid>
                          )}

                          {/* قرارداد — امضای دیجیتال + ضمانت در پورتال پیمانکار */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardHeader title="قرارداد همکاری" />
                              <CardContent>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  قرارداد به‌صورت دیجیتال امضا می‌شود و تصویر چک
                                  ضمانت از{" "}
                                  <strong>پنل پیمانکار (Contractor Portal)</strong>{" "}
                                  بارگذاری می‌گردد؛ آپلود اسکن قرارداد در این صفحه لازم
                                  نیست.
                                </Alert>
                                <Button
                                  variant="outlined"
                                  href="/SLN/Contractor/contracts"
                                  sx={{ mb: registration?.signedContractScanUrl ? 2 : 0 }}
                                >
                                  رفتن به قرارداد و ضمانت
                                </Button>

                                {registration?.signedContractScanUrl ? (
                                  <Box sx={{ mt: 2 }}>
                                    <DocumentViewCard
                                      title="اسکن قرارداد (آرشیو قدیمی)"
                                      url={registration.signedContractScanUrl}
                                      baseUrl={baseUrl}
                                    />
                                  </Box>
                                ) : null}
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* Actions */}
                          <Grid item xs={12}>
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                              <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={onNavigateToListPage}
                              >
                                {isReadOnly ? "بازگشت" : "انصراف"}
                              </Button>
                              {!isReadOnly && (
                                <Button
                                  variant="contained"
                                  type="submit"
                                  disabled={isSubmitting || loading}
                                >
                                  {(loading || isSubmitting) && (
                                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                  )}
                                  ذخیره اطلاعات
                                </Button>
                              )}
                            </Stack>
                          </Grid>
                        </Grid>
                      </form>
                    );
                  }}
                </Formik>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractorInfoModifyPage;
